/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import React, { ChangeEvent, DragEvent, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDropzone } from 'react-dropzone';
import { useParams } from 'react-router-dom';

import Icon from 'carbon-react/lib/components/icon';
import { Option, Select } from 'carbon-react/lib/components/select';

import { ImageResponse } from '@futrli-sdk/predict';
import { Logger } from '@futrli/shared-services';
import { toastError, UIChartsIcon } from '@futrli/ui-components';

import { CARD_TYPES, ImageBehavior } from '../../types';
import { ConfirmModal } from '../modals/confirm-modal';
import { deleteReportImage, getImageUploadLink } from '../../../core/api/reporting/report-builder';
import { SelectImageLevelModal } from '../modals/select-image-level-modal';
import { Spinner } from '../../../ui/spinner/spinner';
import { usePredictionsInfoContext } from '../../../forecast/detail/context/predictions-info-context';
import { useReportBuilderContext } from '../../report-builder.context';

import styles from './sidebar.scss';

export const SidebarImagesPanel = ({
  onItemDragStart
}: {
  onItemDragStart: (
    e: DragEvent<HTMLDivElement | HTMLSpanElement>,
    type: CARD_TYPES,
    data?: Record<string, unknown>
  ) => void;
}) => {
  const intl = useIntl();
  const {
    uploadedImages,
    setUploadedImages,
    currentCard,
    updateSelectedImage,
    isTemplate,
    isMasterPage
  } = useReportBuilderContext();
  const { isConsolidation } = usePredictionsInfoContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File>();
  const { workspaceId: tempWorkspaceId, organisationId: tempOrganisationId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const organisationId = tempOrganisationId || '';
  const [isUploading, setIsUploading] = useState(false);
  const [uploadFailed, setUploadFailed] = useState(false);
  const [imageBehavior, setImageBehavior] = useState<ImageBehavior>(
    currentCard?.[CARD_TYPES.IMAGE]?.behavior || 'original'
  );
  const [imageBeingUploaded, setImageBeingUploaded] = useState<ImageResponse>();
  const [imagePendingDelete, setImagePendingDelete] = useState<string>();
  const [deleteImageModalOpen, setDeleteImageModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSelectImageLevelModalOpen, setIsSelectImageLevelModalOpen] = useState(false);

  const imageBehaviorOptions = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'reporting.original_size', defaultMessage: 'Original size' }),
        value: 'original'
      },
      { name: intl.formatMessage({ id: 'reporting.fit_width', defaultMessage: 'Fit width' }), value: 'fitWidth' },
      { name: intl.formatMessage({ id: 'reporting.fit_height', defaultMessage: 'Fit height' }), value: 'fitHeight' },
      {
        name: intl.formatMessage({ id: 'reporting.stretch_to_fill', defaultMessage: 'Stretch to fill' }),
        value: 'stretch'
      }
    ],
    [intl]
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (/image.*/.exec(acceptedFiles[0].type)) {
        setFile(acceptedFiles[0]);
      } else {
        toastError(
          intl.formatMessage({
            id: 'reporting.file_not_supported_only_image',
            defaultMessage: 'File not supported, you can only upload an image file type.'
          })
        );
      }
    },
    [intl]
  );

  const onSelect = useCallback(() => {
    if (fileInputRef.current?.files?.[0] && /image.*/.exec(fileInputRef.current?.files?.[0].type)) {
      setFile(fileInputRef.current?.files?.[0]);
    } else {
      toastError(
        intl.formatMessage({
          id: 'reporting.file_not_supported_only_image',
          defaultMessage: 'File not supported, you can only upload an image file type.'
        })
      );
    }
  }, [intl]);

  const { getRootProps, isDragActive } = useDropzone({ onDrop });

  const removeImagePendingUpload = useCallback(() => {
    setUploadedImages(uploadedImages.filter(img => img.status === 'ready'));
  }, [uploadedImages, setUploadedImages]);

  useEffect(() => {
    if (file && !imageBeingUploaded && !isUploading) {
      setIsUploading(true);
      setIsSelectImageLevelModalOpen(true);
    }
  }, [organisationId, workspaceId, file, imageBeingUploaded, isUploading]);

  const orgImages = useMemo(() => uploadedImages.filter(img => img.level === 'organisation'), [uploadedImages]);
  const workspaceImages = useMemo(() => uploadedImages.filter(img => img.level === 'workspace'), [uploadedImages]);

  const onImageLevelSelected = (level: 'organisation' | 'workspace') => {
    const uploadFile = async (image: File) => {
      if (image) {
        try {
          const res = await getImageUploadLink(workspaceId, organisationId, image.name, level, isConsolidation);
          if (res.id && res.upload_url) {
            const reader = new FileReader();
            reader.readAsDataURL(image);
            reader.onloadend = () => {
              const url = reader.result as string;
              setImageBeingUploaded({
                id: res.id,
                name: image.name,
                status: 'pending',
                s3_url: url,
                thumbnail_url: url,
                level,
                organisation_id: organisationId,
                workspace_id: workspaceId
              });
            };
            const formData = new FormData();
            Object.entries(res.upload_url.fields).forEach(([field, value]) => {
              formData.append(field, value);
            });
            formData.append('file', image);
            await axios.post(res.upload_url.url, formData, {
              headers: {
                'Content-Type': 'multipart/form-data'
              }
            });
          } else {
            toastError();
          }
        } catch (e) {
          Logger.error(e as string);
          toastError();
          setUploadFailed(true);
        } finally {
          setIsUploading(false);
        }
      }
    };
    setIsSelectImageLevelModalOpen(false);
    if (file) {
      uploadFile(file);
      setFile(undefined);
    }
  };

  useEffect(() => {
    if (uploadFailed) {
      setUploadFailed(false);
      removeImagePendingUpload();
    }
  }, [uploadFailed, removeImagePendingUpload]);

  useEffect(() => {
    if (imageBeingUploaded && !uploadedImages.find(img => img.id === imageBeingUploaded.id)) {
      setUploadedImages([imageBeingUploaded, ...uploadedImages]);
    }
    setImageBeingUploaded(undefined);
  }, [imageBeingUploaded, uploadedImages, setUploadedImages]);

  useEffect(() => {
    setImageBehavior(currentCard?.[CARD_TYPES.IMAGE]?.behavior || 'original');
  }, [currentCard]);

  const selectedImageId = useMemo(() => currentCard?.[CARD_TYPES.IMAGE]?.id, [currentCard]);

  const setSelectedImage = (id: string) => {
    if (currentCard) {
      updateSelectedImage({ id, behavior: imageBehavior });
    }
  };

  const handleImageBehaviorChange = (e: ChangeEvent<HTMLInputElement>) => {
    setImageBehavior(e.target.value as ImageBehavior);
    if (selectedImageId) {
      updateSelectedImage({ id: selectedImageId, behavior: e.target.value as ImageBehavior });
    }
  };

  const handleDeleteImage = (e: MouseEvent, id: string) => {
    e.stopPropagation();
    setImagePendingDelete(id);
    setDeleteImageModalOpen(true);
  };

  const onDeleteImage = async () => {
    setIsDeleting(true);
    if (imagePendingDelete) {
      await deleteReportImage(workspaceId, organisationId, imagePendingDelete, isConsolidation);
      setUploadedImages(uploadedImages.filter(img => img.id !== imagePendingDelete));
    }
    setIsDeleting(false);
    setDeleteImageModalOpen(false);
    setImagePendingDelete('');
  };

  const onCancelDeleteImage = () => {
    setImagePendingDelete('');
    setDeleteImageModalOpen(false);
  };

  const renderImage = (img: ImageResponse) => (
    <div
      className={clsx(styles.imageWrapper, img.id === selectedImageId && styles.selected)}
      key={img.id}
      draggable
      onDragStart={e => onItemDragStart(e, CARD_TYPES.IMAGE, { id: img.id })}
      onClick={() => setSelectedImage(img.id)}
    >
      <img src={img.thumbnail_url} alt={img.name} />
      <UIChartsIcon iconName="delete-icon" onClick={(e: MouseEvent<SVGSVGElement>) => handleDeleteImage(e, img.id)} />
    </div>
  );

  return (
    <div className={clsx(styles.sidebarImagesPanel, isTemplate && styles.templateColor)}>
      <div className={clsx(styles.uploadSection)} {...getRootProps()}>
        {isDragActive ? (
          <FormattedMessage id="reporting.drag_image" defaultMessage="Drag image here" />
        ) : (
          <FormattedMessage id="reporting.upload_image" defaultMessage="Upload image" />
        )}
        <div className={styles.button}>
          <input
            type="file"
            accept="image/*"
            onChange={onSelect}
            ref={fileInputRef}
            aria-label={intl.formatMessage({ id: 'common.upload_file.aria_label', defaultMessage: 'Upload file' })}
          />
          <Icon type="link_cloud" color="#fff" />{' '}
          {file && file.name.length > 16
            ? `${file.name.substring(0, 10)}...${file.name.substring(file.name.lastIndexOf('.'))}`
            : file?.name || <FormattedMessage id="common.browse_files" defaultMessage="Browse Files" />}
        </div>
        {isUploading && (
          <div className={clsx(styles.overlay)}>
            <Spinner />
          </div>
        )}
      </div>
      {selectedImageId && (
        <div className={clsx(styles.imageOptions)}>
          <label htmlFor="behavior">
            <FormattedMessage id="reporting.image_behavior" defaultMessage="Image behavior" />
          </label>
          <Select id="behavior" autoComplete="off" onChange={handleImageBehaviorChange} value={imageBehavior}>
            {imageBehaviorOptions.map(option => (
              <Option key={option.value} value={option.value} text={option.name} />
            ))}
          </Select>
        </div>
      )}
      <h2>
        <FormattedMessage id="reporting.uploaded_images" defaultMessage="Uploaded images" />
      </h2>
      {orgImages.length > 0 && !isMasterPage && (
        <div>
          <h3>
            <FormattedMessage id="reporting.organisation_images" defaultMessage="Organisation level images" />
          </h3>
          <div className={clsx(styles.imagesContainer)}>
            <div className={clsx(styles.half)}>{orgImages.map((img, i) => (i % 2 ? null : renderImage(img)))}</div>
            <div className={clsx(styles.half)}>{orgImages.map((img, i) => (i % 2 ? renderImage(img) : null))}</div>
          </div>
        </div>
      )}
      {workspaceImages.length > 0 && (
        <div>
          <h3>
            <FormattedMessage id="reporting.workspace_images" defaultMessage="Workspace level images" />
          </h3>
          <div className={clsx(styles.imagesContainer)}>
            <div className={clsx(styles.half)}>
              {workspaceImages.map((img, i) => (i % 2 ? null : renderImage(img)))}
            </div>
            <div className={clsx(styles.half)}>
              {workspaceImages.map((img, i) => (i % 2 ? renderImage(img) : null))}
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        onConfirm={onDeleteImage}
        onClose={onCancelDeleteImage}
        isModalOpen={deleteImageModalOpen}
        isWorking={isDeleting}
        title={intl.formatMessage({ id: 'reporting.confirm_delete_image', defaultMessage: 'Confirm Deleting Image' })}
        content={intl.formatMessage({
          id: 'reporting.are_you_sure_delete_image',
          defaultMessage: 'Are you sure you want to delete this image?'
        })}
      />
      <SelectImageLevelModal
        isConsolidation={isConsolidation}
        isModalOpen={isSelectImageLevelModalOpen}
        onConfirm={onImageLevelSelected}
      />
    </div>
  );
};
